import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { StepKinds, FlowStore, buildFlowSearchPayload, buildLookupSearchPayload } from "./FlowTypes";
import { getDatasourceDatabases, getDatasourceFields, executeFlowSearch } from "../../api/flowApi";
import { apiEndpoint } from "../../utils/env";
import "../common/common.css";

// ── Well-known API source keys → resolved endpoint ───────────────────────────
const BUILTIN_SOURCES = {
  "depository.databases": "/api/Flow/datasources/databases",
};

/** Resolve a step's list items from the backend (handles both dataMode:'api' and builtin sources). */
async function resolveListItems(step, ctx, token) {
  // 1. Explicit API mode
  if (step.dataMode === "api" && step.api?.url) {
    const url = interpolate(step.api.url, ctx);
    const absUrl = url.startsWith("http") ? url : apiEndpoint(url);
    try {
      const opts = {
        method: step.api.method || "GET",
        headers: { ...(step.api.headers || {}) },
      };
      if (token) opts.headers["Authorization"] = `Bearer ${token}`;
      if (opts.method === "POST" && !opts.headers["Content-Type"])
        opts.headers["Content-Type"] = "application/json";

      const resp = await fetch(absUrl, opts);
      if (!resp.ok) return [];
      const data = await resp.json();
      const arr = step.api.valuePath
        ? valueAtPath(data, step.api.valuePath)
        : data;
      if (Array.isArray(arr))
        return arr.map((x) => (typeof x === "string" ? x : JSON.stringify(x)));
      return [];
    } catch {
      return [];
    }
  }

  // 2. Built-in source shortcuts
  if (step.source && BUILTIN_SOURCES[step.source]) {
    const endpoint = BUILTIN_SOURCES[step.source];
    try {
      const resp = await fetch(apiEndpoint(endpoint), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) return [];
      return await resp.json();
    } catch {
      return [];
    }
  }

  // 3. Static items
  if (Array.isArray(step.staticItems)) return step.staticItems;

  return [];
}

function interpolate(template, ctx) {
  if (!template) return template;
  return template.replace(/\{\{(.*?)\}\}/g, (_, k) => ctx[k.trim()] ?? "");
}

function valueAtPath(obj, path) {
  if (!path) return obj;
  return path
    .split(".")
    .reduce((o, p) => (o && typeof o === "object" ? o[p] : undefined), obj);
}

/** Check if a lookup step has all required context values to auto-execute.
 *  Steps that use fieldContextKey always need user input (listField + searchValue)
 *  so they must never auto-execute. */
function lookupHasAllContext(step, ctx) {
  const cfg = step.searchConfig || {};
  // If the step relies on fieldContextKey, the user must pick listField and enter
  // a search value interactively — never auto-execute.
  if (cfg.fieldContextKey) return false;
  // Check valueContextKey
  if (cfg.valueContextKey && !ctx[cfg.valueContextKey]) return false;
  // Check fieldMappings
  for (const fm of (cfg.fieldMappings || [])) {
    if (fm.contextKey && !ctx[fm.contextKey]) return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function FlowRunnerCore({
  flow = null,         // pre-loaded flow object (preferred)
  initialFlowName = "",
  token = null,
  onClose = null,
  showHeader = false,
  showContextPanel = false,
  onResults = null,    // callback(results) when search completes – if provided, results are pushed externally
  onClear = null,      // callback() when user resets or changes a step (clears external results)
}) {
  const rootRef = React.useRef(null);
  const { t } = useTranslation();
  const [entered, setEntered] = React.useState(false);

  // entrance animation
  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    setEntered(true);
    try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    const timer = setTimeout(() => setEntered(false), 700);
    return () => clearTimeout(timer);
  }, [initialFlowName]);

  // Resolve active flow: prefer prop, else find by name from cache
  const [cachedFlows] = useState(() => FlowStore.loadAll());
  const activeFlow = useMemo(() => {
    if (flow) return flow;
    if (!initialFlowName) return cachedFlows[0] || null;
    return cachedFlows.find((f) => f.name === initialFlowName) || null;
  }, [flow, initialFlowName, cachedFlows]);

  const [ctx, setCtx] = useState({});
  const [lists, setLists] = useState({});
  const [lookups, setLookups] = useState({}); // index -> { rows, loading, error }
  const [visibleCount, setVisibleCount] = useState(
    activeFlow?.steps?.length ? 1 : 0
  );
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = not searched yet
  const [searchError, setSearchError] = useState("");
  const [searchDone, setSearchDone] = useState(false);

  const total = activeFlow?.steps?.length || 0;

  // Reset when flow changes
  useEffect(() => {
    setCtx({});
    setLists({});
    setLookups({});
    setVisibleCount(activeFlow?.steps?.length ? 1 : 0);
    setSearchResults(null);
    setSearchError("");
    setSearchDone(false);
  }, [activeFlow?.name, activeFlow?.steps?.length]);

  // Load list data for newly visible list steps
  const loadListFor = useCallback(
    async (index, st, currentCtx) => {
      const items = await resolveListItems(st, currentCtx, token);
      setLists((prev) => ({ ...prev, [index]: items }));
    },
    [token]
  );

  // Execute a lookup step's search
  const executeLookup = useCallback(
    async (index, st, currentCtx) => {
      if (!token) return;
      setLookups((prev) => ({ ...prev, [index]: { rows: [], loading: true, error: "" } }));
      try {
        const payload = buildLookupSearchPayload(st, currentCtx);
        const results = await executeFlowSearch(token, payload.context, payload.searchConfig);
        const rows = Array.isArray(results) ? results : [];
        setLookups((prev) => ({ ...prev, [index]: { rows, loading: false, error: "" } }));
      } catch (e) {
        setLookups((prev) => ({
          ...prev,
          [index]: { rows: [], loading: false, error: e.message || "Lookup failed" },
        }));
      }
    },
    [token]
  );

  // Execute a lookup with an overridden searchConfig (built at runtime by user)
  const executeLookupWith = useCallback(
    async (index, st, currentCtx, overrideSearchConfig) => {
      if (!token) return;
      setLookups((prev) => ({ ...prev, [index]: { rows: [], loading: true, error: "" } }));
      try {
        const cfg = overrideSearchConfig || st.searchConfig || {};
        const results = await executeFlowSearch(token, currentCtx, cfg);
        const rows = Array.isArray(results) ? results : [];
        setLookups((prev) => ({ ...prev, [index]: { rows, loading: false, error: "" } }));
      } catch (e) {
        setLookups((prev) => ({ ...prev, [index]: { rows: [], loading: false, error: e.message || "Lookup failed" } }));
      }
    },
    [token]
  );

  useEffect(() => {
    if (!activeFlow) return;
    for (let i = 0; i < Math.min(visibleCount, total); i++) {
      const st = activeFlow.steps[i];
      if (st?.kind === StepKinds.list && !lists[i]) {
        loadListFor(i, st, ctx);
      }
      if (st?.kind === StepKinds.lookup && !lookups[i]) {
        // Only auto-execute if all required context values are present
        if (lookupHasAllContext(st, ctx)) {
          executeLookup(i, st, ctx);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, activeFlow, total, ctx]);

  const revealNext = (fromIndex) => {
    if (!activeFlow) return;
    const nextIndex = fromIndex + 1;
    if (nextIndex >= total) {
      // All steps completed – execute search
      triggerSearch();
      return;
    }
    // Clear stale data so useEffect reloads with committed (correct) ctx
    setLists((prev) => { const n = { ...prev }; delete n[nextIndex]; return n; });
    setLookups((prev) => { const n = { ...prev }; delete n[nextIndex]; return n; });
    setVisibleCount((c) => Math.max(c, nextIndex + 1));
    // useEffect handles loading with the correct ctx
  };

  const setCtxValAt = (index, key, value) => {
    const newCtx = { ...ctx, [key]: value };
    setCtx(newCtx);
    setSearchDone(false);
    setSearchResults(null);
    setSearchError("");
    if (onClear && searchDone) onClear();
    if (index < visibleCount - 1) {
      setLists((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          if (Number(k) <= index) next[k] = prev[k];
        });
        return next;
      });
      setLookups((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          if (Number(k) <= index) next[k] = prev[k];
        });
        return next;
      });
      setVisibleCount(index + 1);
    }
  };

  const triggerSearch = async () => {
    if (!activeFlow || !token) {
      setSearchError(t("flow.runner.noToken", "Authentication required."));
      return;
    }
    setSearching(true);
    setSearchError("");
    setSearchResults(null);
    try {
      const payload = buildFlowSearchPayload(activeFlow, ctx);
      const results = await executeFlowSearch(token, payload.context, payload.searchConfig);
      const rows = Array.isArray(results) ? results : [];
      setSearchResults(rows);
      setSearchDone(true);
      if (onResults) onResults(rows);
    } catch (e) {
      setSearchError(e.message || t("flow.runner.searchFailed", "Search failed."));
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setCtx({});
    setLists({});
    setLookups({});
    setVisibleCount(activeFlow?.steps?.length ? 1 : 0);
    setSearchResults(null);
    setSearchError("");
    setSearchDone(false);
    if (onClear) onClear();
  };

  return (
    <div
      ref={rootRef}
      className={`flow-runner-root ${entered ? "flow-enter" : ""}`}
    >
      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="ai-chip">{activeFlow?.name || t("flow.runner.title", "Flow Runner")}</div>
          <div style={{ flex: 1 }} />
          {onClose && (
            <button
              className="drawer-close-btn"
              onClick={onClose}
              aria-label={t("common.close")}
              title={t("common.close")}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: 12, marginTop: showHeader ? 12 : 0 }}>
        {!activeFlow && (
          <div style={{ color: "#888" }}>{t("flow.noFlow", "No flow selected.")}</div>
        )}

        {activeFlow &&
          activeFlow.steps
            .slice(0, visibleCount)
            .map((step, i) => (
              step.kind === StepKinds.lookup ? (
                <LookupStepView
                  key={i}
                  index={i}
                  step={step}
                  ctx={ctx}
                  token={token}
                  lookupData={lookups[i] || { rows: [], loading: false, error: "" }}
                  onExecute={(injectedCtx, overrideSearchConfig) => {
                    setCtx(injectedCtx);
                    executeLookupWith(i, step, injectedCtx, overrideSearchConfig);
                  }}
                  onSelect={(selectedCtx) => {
                    const newCtx = { ...ctx, ...selectedCtx };
                    setCtx(newCtx);
                    setSearchError("");
                    if (onClear && searchDone) onClear();
                    // Clear subsequent steps if not last
                    if (i < visibleCount - 1) {
                      setLists((prev) => {
                        const next = {};
                        Object.keys(prev).forEach((k) => { if (Number(k) <= i) next[k] = prev[k]; });
                        return next;
                      });
                      setLookups((prev) => {
                        const next = {};
                        Object.keys(prev).forEach((k) => { if (Number(k) <= i) next[k] = prev[k]; });
                        return next;
                      });
                      setVisibleCount(i + 1);
                      setTimeout(() => revealNext(i), 80);
                    } else {
                      // Lookup is the last step — its rows ARE the final results
                      const lookupRows = lookups[i]?.rows || [];
                      setSearchResults(lookupRows);
                      setSearchDone(true);
                      if (onResults) onResults(lookupRows);
                    }
                  }}
                  onRetry={() => executeLookup(i, step, ctx)}
                  onReset={(clearKeys) => {
                    const newCtx = { ...ctx };
                    for (const k of (clearKeys || [])) delete newCtx[k];
                    setCtx(newCtx);
                    setLookups((prev) => { const n = { ...prev }; delete n[i]; return n; });
                    setSearchDone(false);
                    setSearchResults(null);
                    setSearchError("");
                    if (onClear) onClear();
                    if (i < visibleCount - 1) {
                      setLists((prev) => {
                        const next = {};
                        Object.keys(prev).forEach((k) => { if (Number(k) <= i) next[k] = prev[k]; });
                        return next;
                      });
                      setVisibleCount(i + 1);
                    }
                  }}
                />
              ) : (
                <StepView
                  key={i}
                  index={i}
                  step={step}
                  ctx={ctx}
                  listData={lists[i] || []}
                  setCtxVal={(k, v) => setCtxValAt(i, k, v)}
                  onEnter={() => revealNext(i)}
                />
              )
            ))}

        {/* Search trigger — hidden when last step is a lookup (it handles its own results) */}
        {activeFlow && visibleCount >= total && !searchDone && !searching &&
         activeFlow.steps[total - 1]?.kind !== StepKinds.lookup && (
          <button
            className="go-btn"
            onClick={triggerSearch}
            disabled={!token}
            style={{ marginTop: 4 }}
          >
            {t("flow.runner.search", "Search")}
          </button>
        )}

        {searching && (
          <div className="skeleton-line" style={{ width: "50%", height: 20 }} />
        )}

        {searchError && (
          <div style={{ color: "#c00", fontSize: 13 }}>{searchError}</div>
        )}
      </div>

      {/* Inline results — only shown when no external onResults handler */}
      {!onResults && searchResults !== null && (
        <SearchResultsList
          results={searchResults}
          token={token}
          onReset={reset}
        />
      )}

      {showContextPanel && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ margin: "8px 0 4px" }}>{t("flow.context", "Context")}</h3>
          <pre style={styles.pre}>{JSON.stringify(ctx, null, 2)}</pre>
          <div style={{ fontSize: 12, color: "#888" }}>
            {t("flow.visible", { visible: visibleCount, total })}
          </div>
          <ol style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {activeFlow?.steps?.map((s, i) => (
              <li key={i} style={{ fontWeight: i < visibleCount ? 600 : 400, fontSize: 12 }}>
                {s.kind} — {s.label || s.source || ""}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline search results
// ─────────────────────────────────────────────────────────────────────────────

function SearchResultsList({ results, token, onReset }) {
  const { t } = useTranslation();

  if (!results.length) {
    return (
      <div style={styles.resultsBox}>
        <div style={{ color: "#888", fontSize: 13 }}>
          {t("flow.runner.noResults", "No documents found.")}
        </div>
        <button className="link-btn" onClick={onReset} style={{ marginTop: 8 }}>
          {t("flow.runner.newSearch", "New Search")}
        </button>
      </div>
    );
  }

  return (
    <div style={styles.resultsBox}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>
          {t("flow.runner.resultsCount", { count: results.length }, `${results.length} result(s) found`)}
        </strong>
        <button className="link-btn" onClick={onReset} style={{ marginLeft: "auto" }}>
          {t("flow.runner.newSearch", "New Search")}
        </button>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {results.map((doc, i) => (
          <DocResultItem key={doc.itemId || doc.id || i} doc={doc} token={token} />
        ))}
      </ul>
    </div>
  );
}

function DocResultItem({ doc, token }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [open, setOpen] = useState(false);

  const title =
    doc.originalName ||
    doc.fileName ||
    doc.archiveNo ||
    doc.itemId ||
    `Document #${doc.id}`;

  const indexes = doc.searchIndexes || doc.SearchIndexes || {};

  const openDoc = async () => {
    if (blobUrl) { setOpen(true); return; }
    if (!doc.itemId || !token) return;
    setLoading(true);
    try {
      const resp = await fetch(apiEndpoint("/api/Document/ViewDocument"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ requests: [{ requestType: 1, link: doc.itemId }] }),
      });
      if (resp.ok) {
        const blob = await resp.blob();
        setBlobUrl(URL.createObjectURL(blob));
        setOpen(true);
      }
    } catch {}
    finally { setLoading(false); }
  };

  return (
    <li style={styles.docItem}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
          {Object.keys(indexes).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 4 }}>
              {Object.entries(indexes).slice(0, 6).map(([k, v]) => (
                <span key={k} style={{ fontSize: 11, color: "#555" }}>
                  <span style={{ fontWeight: 500 }}>{k}:</span> {v}
                </span>
              ))}
            </div>
          )}
        </div>
        {doc.itemId && (
          <button
            className="icon-btn"
            onClick={openDoc}
            disabled={loading}
            title={t("flow.runner.viewDoc", "View document")}
          >
            {loading ? "…" : "↗"}
          </button>
        )}
      </div>
      {open && blobUrl && (
        <div style={{ marginTop: 8 }}>
          <iframe
            src={blobUrl}
            title={title}
            style={{ width: "100%", height: 500, border: "1px solid #ccc", borderRadius: 4 }}
          />
          <button className="link-btn" onClick={() => setOpen(false)} style={{ marginTop: 4 }}>
            {t("common.close")}
          </button>
        </div>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step rendering
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Lookup step rendering (mid-flow search with selectable table)
// ─────────────────────────────────────────────────────────────────────────────

function LookupStepView({ step, index, ctx, lookupData, onSelect, onExecute, onReset, token }) {
  const { t } = useTranslation();
  const { rows, loading, error } = lookupData;
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [listFields, setListFields]   = useState([]); // multi-select columns to display
  const [searchValue, setSearchValue] = useState("");
  const [runtimeFields, setRuntimeFields] = useState(null);
  const [searched, setSearched]       = useState(false);

  const cfg          = step.searchConfig || {};
  const hasSelection = !!(ctx[step.key]);
  const dbName       = cfg.databaseContextKey ? ctx[cfg.databaseContextKey] : (cfg.databases || [])[0] || "";
  const filterField  = cfg.fieldContextKey    ? ctx[cfg.fieldContextKey]    : "";

  // Load fields once per database
  const loadedDbRef = React.useRef(null);
  useEffect(() => {
    if (!token || !dbName || loadedDbRef.current === dbName) return;
    loadedDbRef.current = dbName;
    setRuntimeFields(null);
    fetch(apiEndpoint(`/api/Flow/datasources/fields/${encodeURIComponent(dbName)}`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((fields) => setRuntimeFields(Array.isArray(fields) ? fields : []))
      .catch(() => setRuntimeFields([]));
  }, [token, dbName]);

  const canSearch = listFields.length > 0 && searchValue.trim() !== "";

  const handleSearch = () => {
    if (!canSearch) return;
    setSearched(true);
    const injected = { ...ctx, __lookupVal: searchValue };
    const sc = {
      ...(cfg.databaseContextKey
        ? { databaseContextKey: cfg.databaseContextKey }
        : { databases: cfg.databases || [] }),
      fieldMappings: [{ contextKey: "__lookupVal", fieldName: filterField, type: "value" }],
    };
    onExecute(injected, sc);
  };

  const handleNewSearch = () => {
    const clearKeys = [step.key];
    for (const m of (step.contextMappings || [])) if (m.to) clearKeys.push(m.to);
    for (const f of listFields) if (f) clearKeys.push(f);
    setSearched(false);
    setSearchValue("");
    setListFields([]);
    setSelectedIdx(null);
    if (onReset) onReset(clearKeys);
  };

  // Columns = user-selected fields, or fallback to step config / derived
  const columns = listFields.length > 0 ? listFields
    : step.columns?.length > 0 ? step.columns
    : rows.length > 0 ? deriveColumns(rows[0]) : [];

  const card  = hasSelection ? { ...lookupCard, borderLeftColor: "#16a34a", borderLeftWidth: 3 } : lookupCard;
  const numBg = hasSelection ? "#16a34a" : "#3b5bdb";

  const handleRowSelect = (row, rowIdx) => {
    setSelectedIdx(rowIdx);
    const newCtx = {};
    // First listField is the primary value
    const primary = listFields[0] || step.selectField || columns[0];
    if (primary) newCtx[step.key] = getField(row, primary);
    for (const m of (step.contextMappings || []))
      if (m.from && m.to) newCtx[m.to] = getField(row, m.from);
    // Also write each listed field into context under its own name
    for (const f of listFields)
      if (f !== primary) newCtx[f] = getField(row, f);
    onSelect(newCtx);
  };

  const showForm = !hasSelection && (!searched || rows.length === 0);

  return (
    <div style={card}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ ...lookupNum, background: numBg }}>{index + 1}</span>
        <div style={{ flex: 1 }}>
          <div style={lookupLabel}>{step.label || t("flow.lookup.title", "Lookup")}</div>
          {hasSelection && (
            <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, marginTop: 2 }}>✓ {ctx[step.key]}</div>
          )}
        </div>
        {(hasSelection || (searched && rows.length > 0)) && !loading && (
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 11 }}>
            {hasSelection
              ? t("flow.lookup.change", "Change")
              : t("flow.lookup.newSearch", "New search")}
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ display: "grid", gap: 10 }}>

          {/* List by — multi chip picker */}
          <div>
            <div style={lookupFieldLabel}>{t("flow.lookup.listBy", "List by")}</div>
            {runtimeFields === null ? (
              dbName
                ? <div className="skeleton-line" style={{ width: "100%", height: 34 }} />
                : <div style={{ fontSize: 12, color: "#aaa" }}>{t("flow.lookup.noDb", "No database selected in previous steps")}</div>
            ) : (
              <FieldChipPicker
                fields={runtimeFields}
                selected={listFields}
                onChange={setListFields}
              />
            )}
          </div>

          {/* Where [filterField] = value — search button inlined */}
          {filterField && (
            <div>
              <div style={lookupFieldLabel}>
                {t("flow.lookup.where", "Where")} <strong>{filterField}</strong> =
              </div>
              <div style={{ position: "relative" }}>
                <input style={{ ...lookupInput, paddingRight: 44 }}
                  placeholder={filterField}
                  value={searchValue}
                  onChange={(e) => { setSearchValue(e.target.value); if (searched) setSearched(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  onFocus={(e) => { e.target.style.borderColor = "#a5b4fc"; e.target.style.boxShadow = "0 0 0 3px rgba(59,91,219,.10)"; }}
                  onBlur={(e)  => { e.target.style.borderColor = "#e2e5ef"; e.target.style.boxShadow = "none"; }}
                />
                <button
                  onClick={handleSearch}
                  disabled={!canSearch || loading}
                  title={t("flow.runner.search", "Search")}
                  style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: canSearch && !loading ? "#3b5bdb" : "#e2e5ef", border: "none", borderRadius: 6, width: 30, height: 26, cursor: canSearch && !loading ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }}
                >
                  {loading
                    ? <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                    : <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="5.5" cy="5.5" r="4" stroke={canSearch ? "#fff" : "#aaa"} strokeWidth="1.5"/><line x1="8.5" y1="8.5" x2="12" y2="12" stroke={canSearch ? "#fff" : "#aaa"} strokeWidth="1.5" strokeLinecap="round"/></svg>
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && <div className="skeleton-line" style={{ width: "70%", height: 20, marginTop: 8 }} />}

      {error && !loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 13, color: "#c00" }}>{error}</span>
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 12 }}>
            {t("flow.lookup.retry", "Retry")}
          </button>
        </div>
      )}

      {searched && !loading && !error && rows.length === 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
          <span style={{ fontSize: 13, color: "#888" }}>
            {t("flow.lookup.noResults", "No results found.")}
            {filterField && searchValue && <span style={{ color: "#bbb" }}> ({filterField} = "{searchValue}")</span>}
          </span>
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {t("flow.lookup.newSearch", "Try again")}
          </button>
        </div>
      )}

      {/* Results table */}
      {!loading && !error && rows.length > 0 && (
        <div style={lookupTableWrap}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
            {t("flow.lookup.resultsCount", { count: rows.length }, `${rows.length} result(s)`)}
            {" — "}
            {t("flow.lookup.selectRow", "click a row to select")}
          </div>
          <table style={lookupTable}>
            <thead>
              <tr>{columns.map((col) => <th key={col} style={lookupTh}>{col}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} onClick={() => handleRowSelect(row, ri)}
                  style={{ ...lookupTr, background: selectedIdx === ri ? "#eef3ff" : ri % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer", outline: selectedIdx === ri ? "2px solid #3b5bdb" : "none" }}>
                  {columns.map((col) => <td key={col} style={lookupTd}>{formatCell(getField(row, col))}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Inline chip-based multi-field picker for the runtime lookup form. */
function FieldChipPicker({ fields, selected, onChange }) {
  const remaining = fields.filter((f) => !selected.includes(f));
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      {selected.map((f) => (
        <span key={f} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 10px", background: "#eef3ff", border: "1.5px solid #a5b4fc", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#3b5bdb" }}>
          {f}
          <button type="button" onClick={() => onChange(selected.filter((x) => x !== f))}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>✕</button>
        </span>
      ))}
      {remaining.length > 0 && (
        <select style={lookupSelect} value=""
          onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }}>
          <option value="">{selected.length ? "+ add field" : "— select fields —"}</option>
          {remaining.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      )}
    </div>
  );
}


const lookupFieldLabel = { fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 };
const lookupInput = { width: "100%", padding: "7px 10px", border: "1.5px solid #e2e5ef", borderRadius: 8, fontSize: 13, color: "#1a1a2e", background: "#f9fafb", outline: "none", fontFamily: "inherit", transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box" };
const lookupSelect = { width: "100%", padding: "7px 10px", border: "1.5px solid #e2e5ef", borderRadius: 8, fontSize: 13, color: "#1a1a2e", background: "#f9fafb", outline: "none", fontFamily: "inherit", appearance: "auto" };


const lookupCard = { border: "1px solid #e2e5ef", borderRadius: 8, padding: "10px 12px", background: "#fff" };
const lookupLabel = { fontSize: 13, fontWeight: 600, color: "#1a1a2e" };
const lookupNum = { width: 20, height: 20, borderRadius: "50%", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff" };
const lookupTableWrap = { overflowX: "auto", maxHeight: 240, overflowY: "auto" };
const lookupTable = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const lookupTh = { padding: "6px 8px", textAlign: "left", fontWeight: 600, borderBottom: "2px solid #e2e5ef", color: "#1a1a2e", whiteSpace: "nowrap", position: "sticky", top: 0, background: "#fff" };
const lookupTd = { padding: "5px 8px", borderBottom: "1px solid #f0f0f0", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const lookupTr = { transition: "background .1s" };

function getField(row, fieldName) {
  // Exact match first
  if (row[fieldName] !== undefined) return row[fieldName];
  // Case-insensitive match on top-level row properties
  const lower = fieldName.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) return row[k];
  }
  // Check searchIndexes (custom indexed fields)
  const indexes = row.searchIndexes || row.SearchIndexes || {};
  if (indexes[fieldName] !== undefined) return indexes[fieldName];
  for (const k of Object.keys(indexes)) {
    if (k.toLowerCase() === lower) return indexes[k];
  }
  return null; // null = no value found (renders as "—")
}
function deriveColumns(row) {
  const skip = new Set(["itemId", "id", "searchIndexes", "SearchIndexes"]);
  const cols = [];
  for (const k of Object.keys(row)) { if (!skip.has(k) && typeof row[k] !== "object") cols.push(k); }
  const indexes = row.searchIndexes || row.SearchIndexes || {};
  for (const k of Object.keys(indexes)) { if (!cols.includes(k)) cols.push(k); }
  return cols.slice(0, 6);
}
function formatCell(val) {
  if (val == null || val === "") return "—";
  // ISO date string — format as date only, suppress default C# DateTime (0001-01-01)
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    if (val.startsWith("0001-")) return "—";
    return val.slice(0, 10); // "YYYY-MM-DD"
  }
  if (typeof val === "object") return JSON.stringify(val);
  const s = String(val);
  return s === "" ? "—" : s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Regular step rendering
// ─────────────────────────────────────────────────────────────────────────────

const stepCard = {
  border: "1px solid #e2e5ef",
  borderRadius: 8,
  padding: "10px 12px",
  background: "#fff",
};
const stepLabel = {
  fontSize: 13, fontWeight: 600, color: "#1a1a2e",
};
const stepKey = {
  fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
  textTransform: "uppercase", color: "#9ca3af", marginTop: 1,
};
const stepInput = {
  width: "100%", padding: "7px 10px",
  border: "1.5px solid #e2e5ef", borderRadius: 8,
  fontSize: 13, color: "#1a1a2e", background: "#f9fafb",
  outline: "none", fontFamily: "inherit",
  transition: "border-color .15s, box-shadow .15s",
};
const stepSelect = { ...stepInput, appearance: "auto" };
const stepNum = {
  width: 20, height: 20, borderRadius: "50%",
  fontSize: 10, fontWeight: 700,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0, color: "#fff",
};

function StepView({ step, index, ctx, setCtxVal, listData, onEnter }) {
  const resolve = (s) =>
    (s || "").replace(/\{\{(.*?)\}\}/g, (_, k) => ctx[k.trim()] ?? `{{${k.trim()}}}`);

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (
      step.kind === StepKinds.input &&
      (ctx[step.key] ?? "").toString().trim() !== ""
    )
      return onEnter();
    if (step.kind === StepKinds.list && ctx[step.key]) return onEnter();
    if (
      step.kind === StepKinds.dateRange &&
      ctx[step.fromKey] &&
      ctx[step.toKey]
    )
      return onEnter();
  };

  const hasValue =
    step.kind === StepKinds.dateRange
      ? !!(ctx[step.fromKey] && ctx[step.toKey])
      : !!(ctx[step.key]);

  const card = hasValue
    ? { ...stepCard, borderLeftColor: "#16a34a", borderLeftWidth: 3 }
    : stepCard;
  const numBg = hasValue ? "#16a34a" : "#3b5bdb";

  if (step.kind === StepKinds.input) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ ...stepNum, background: numBg }}>{index + 1}</span>
          <div>
            <div style={stepLabel}>{resolve(step.label)}</div>
            <div style={stepKey}>{step.key}</div>
          </div>
        </div>
        <input
          style={stepInput}
          placeholder={resolve(step.placeholder) || "Enter value…"}
          value={ctx[step.key] || ""}
          onChange={(e) => setCtxVal(step.key, e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => { e.target.style.borderColor = "#a5b4fc"; e.target.style.boxShadow = "0 0 0 3px rgba(59,91,219,.10)"; }}
          onBlur={(e) => { e.target.style.borderColor = "#e2e5ef"; e.target.style.boxShadow = "none"; }}
        />
      </div>
    );
  }

  if (step.kind === StepKinds.list) {
    const selected = ctx[step.key] || "";
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ ...stepNum, background: numBg }}>{index + 1}</span>
          <div>
            <div style={stepLabel}>{resolve(step.label)}</div>
            <div style={stepKey}>{step.key}</div>
          </div>
        </div>
        {!listData.length ? (
          <div className="skeleton-line" style={{ width: "60%", height: 32 }} />
        ) : (
          <select
            style={stepSelect}
            value={selected}
            onChange={(e) => {
              const v = e.target.value;
              setCtxVal(step.key, v);
              if (v) setTimeout(() => onEnter(), 80);
            }}
          >
            <option value="">— select —</option>
            {listData.map((x, i) => (
              <option key={i} value={x}>{x}</option>
            ))}
          </select>
        )}
      </div>
    );
  }

  if (step.kind === StepKinds.dateRange) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ ...stepNum, background: numBg }}>{index + 1}</span>
          <div>
            <div style={stepLabel}>{resolve(step.label)}</div>
            <div style={stepKey}>{step.fromKey} / {step.toKey}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="date"
            style={{ ...stepInput, flex: 1 }}
            value={ctx[step.fromKey] || ""}
            onChange={(e) => setCtxVal(step.fromKey, e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <input
            type="date"
            style={{ ...stepInput, flex: 1 }}
            value={ctx[step.toKey] || ""}
            onChange={(e) => setCtxVal(step.toKey, e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
    );
  }

  return null;
}

const styles = {
  pre: {
    background: "#111",
    color: "#f1f1f1",
    padding: 12,
    borderRadius: 8,
    overflow: "auto",
    fontSize: 11,
  },
  resultsBox: {
    marginTop: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
  },
  docItem: {
    padding: "10px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    marginBottom: 8,
    background: "#fff",
  },
};
